import { Box, Checkbox, Text } from '@chakra-ui/react';
import {
  Select as ChakraSelect,
  type GroupBase,
  type SingleValue,
} from 'chakra-react-select';
import { useEffect } from 'react';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../PageConfigProvider/page-reducer';
import type {
  SecondaryDimensionOption,
  SecondaryDimensionValues,
} from '../PageConfigProvider/types/dimension';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import { BaseSelectStyles } from './BaseSelectStyles';
import { getAvailableSegments } from './utils/segment-mapping';

type SecondaryDimensionSelectorProps = {
  item: PositionedItem;
  primaryFilter: PrimaryFilterConfig;
  forceSegment?: boolean;
  isDisabled?: boolean;
  showGroupBySegment?: boolean;
  label?: string;
  excludeDimensions?: SecondaryDimensionValues[];
};

export const SecondaryDimensionSelector = ({
  item,
  primaryFilter,
  forceSegment = false,
  isDisabled = false,
  showGroupBySegment = false,
  label = 'Segment',
  excludeDimensions,
}: SecondaryDimensionSelectorProps) => {
  const { dispatch } = useTabContentConfig();
  const id = item?.id;

  const { secondaryDimension, groupBySegment } = item?.plot || {};
  const primaryFilterId = primaryFilter?.id;
  const metrics = item?.plot?.defaultMetricOptions;

  const allDimensions = getAvailableSegments({
    chartType: item?.plot?.chartType,
    primaryFilterId,
    metrics,
  });
  const availableSecondaryDimensions =
    excludeDimensions && excludeDimensions.length > 0
      ? allDimensions.filter((dim) => !excludeDimensions.includes(dim.value))
      : allDimensions;

  const selectedSecondaryDimension =
    availableSecondaryDimensions.find(
      (dimension) => dimension.value === secondaryDimension
    ) ?? (forceSegment ? availableSecondaryDimensions?.[0] : null);

  useEffect(() => {
    if (
      (selectedSecondaryDimension === null ||
        selectedSecondaryDimension?.value === null ||
        !availableSecondaryDimensions.length) &&
      secondaryDimension !== undefined
    ) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
        replacePreviousHistorySnapshot: true,
      });
    }

    if (
      selectedSecondaryDimension &&
      secondaryDimension !== selectedSecondaryDimension.value
    ) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: selectedSecondaryDimension.value },
      });
    }
  }, [
    availableSecondaryDimensions,
    id,
    dispatch,
    selectedSecondaryDimension,
    secondaryDimension,
  ]);

  const onChangeSecondaryDimension = (
    secondaryDimension: SingleValue<SecondaryDimensionOption>
  ) => {
    if (!secondaryDimension) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
      });
      return;
    }

    if (secondaryDimension.value === null) {
      dispatch({
        type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
        payload: { id, secondaryDimension: undefined },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_ITEM_SECONDARY_DIMENSION',
      payload: { id, secondaryDimension: secondaryDimension.value },
    });
  };

  const onChangeGroupBySegment = (groupBySegment: boolean) => {
    dispatch({
      type: 'UPDATE_ITEM_GROUP_BY_SEGMENT',
      payload: { id, groupBySegment },
    });
  };

  if (availableSecondaryDimensions.length === 0) return null;

  return (
    <Box marginBottom="1.5rem">
      <Text
        fontSize="sm"
        fontWeight={600}
        marginBottom="0.5rem"
        opacity={isDisabled ? 0.6 : 1}
      >
        {label}
      </Text>
      <ChakraSelect<
        SecondaryDimensionOption,
        false,
        GroupBase<SecondaryDimensionOption>
      >
        chakraStyles={BaseSelectStyles}
        options={availableSecondaryDimensions}
        value={selectedSecondaryDimension}
        onChange={onChangeSecondaryDimension}
        isClearable={!forceSegment}
        isSearchable
        isDisabled={isDisabled}
      />
      {showGroupBySegment && (
        <Checkbox
          isChecked={groupBySegment}
          onChange={(e) => onChangeGroupBySegment(e.target.checked)}
          colorScheme="green"
          marginTop=".75rem"
          isDisabled={isDisabled}
          sx={{
            '> .chakra-checkbox__label': {
              fontSize: 'sm',
            },
          }}
        >
          Group by Segment
        </Checkbox>
      )}
    </Box>
  );
};
